import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Eye, EyeOff, Save, Send } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

export default function AdminEmailSettings() {
  const [f, setF] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tpls, setTpls] = useState([]);
  const [savingTpl, setSavingTpl] = useState(null);
  const { tf: t } = useLang();

  useEffect(() => {
    api.get("/admin/email-settings").then((r) => setF(r.data)).catch(() => {});
    api.get("/admin/email-templates").then((r) => setTpls(r.data)).catch(() => {});
  }, []);
  if (!f) return <div className="py-20 text-center text-ink-secondary">{t("adm_loading")}</div>;

  const setTpl = (i, k, v) => setTpls(tpls.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const saveTpl = async (tp) => {
    setSavingTpl(tp.key);
    try {
      await api.put(`/admin/email-templates/${tp.key}`, { subject: tp.subject, body: tp.body });
      toast.success(`Modèle « ${tp.label} » enregistré.`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingTpl(null); }
  };

  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-settings", {
        sender_email: f.sender_email, sender_password: f.sender_password,
        sender_name: f.sender_name, smtp_host: f.smtp_host, smtp_port: Number(f.smtp_port) || 587,
      });
      setF(data);
      toast.success(t("eml_saved"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/email-settings/test");
      if (data.ok) toast.success(data.detail, { duration: 7000 });
      else toast.warning(data.detail, { duration: 9000 });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTesting(false); }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("eml_kicker")}</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white">{t("eml_title")}</h1>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink-secondary">
        {t("eml_desc")}
      </p>

      {/* Section « smtp » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-smtp">Serveur SMTP</div>
      <div data-testid="email-panel-smtp">
      <div className="mt-6 space-y-5 border border-white/10 bg-surface p-6">
        <div className="flex items-center gap-2 text-sm font-600 text-white"><Mail size={18} className="text-cyan" /> {t("eml_account")}</div>

        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("eml_sender_name")}</span>
          <input value={f.sender_name || ""} onChange={(e) => set("sender_name", e.target.value)} data-testid="email-sender-name"
            className="w-full border border-white/10 bg-void px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("eml_sender_email")}</span>
          <input value={f.sender_email || ""} onChange={(e) => set("sender_email", e.target.value)} data-testid="email-sender-address"
            className="w-full border border-white/10 bg-void px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("eml_password")}</span>
          <div className="relative">
            <input type={show ? "text" : "password"} value={f.sender_password || ""} onChange={(e) => set("sender_password", e.target.value)} data-testid="email-password"
              className="w-full border border-white/10 bg-void px-4 py-3 pr-12 text-sm text-white outline-none focus:border-cyan" />
            <button type="button" onClick={() => setShow(!show)} data-testid="email-toggle-pwd" className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-secondary hover:text-white">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("eml_smtp")}</span>
            <input value={f.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} placeholder="ex : smtp.orange.fr" data-testid="email-smtp-host"
              className="w-full border border-white/10 bg-void px-4 py-3 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("eml_port")}</span>
            <input type="number" value={f.smtp_port || 587} onChange={(e) => set("smtp_port", e.target.value)} data-testid="email-smtp-port"
              className="w-full border border-white/10 bg-void px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
          </label>
        </div>

        <div className="flex flex-wrap gap-3">
          <button onClick={save} disabled={saving} data-testid="email-save"
            className="aca-btn-primary inline-flex items-center gap-2 hover:-translate-y-0.5 disabled:opacity-60">
            <Save size={16} /> {saving ? t("eml_saving") : t("eml_save")}
          </button>
          <button onClick={sendTest} disabled={testing} data-testid="email-test"
            className="inline-flex items-center gap-2 rounded-full border border-cyan px-7 py-3.5 text-sm font-600 uppercase tracking-wide text-cyan transition-all hover:-translate-y-0.5 hover:bg-cyan hover:text-void disabled:opacity-60">
            <Send size={16} /> {testing ? "Test en cours..." : "Tester l'envoi"}
          </button>
        </div>
      </div>

      <p className="mt-4 text-xs leading-relaxed text-ink-tertiary">
        {t("eml_note")}
      </p>
      </div>
      </>

      {/* Modèles d'e-mail paramétrables */}
      {/* Section « modeles » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-modeles">Modèles d'e-mail</div>
      <div className="mt-6" data-testid="email-templates">
        <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Modèles d&apos;e-mail</div>
        <h2 className="mt-3 font-display text-2xl font-700 text-white">Contenu de chaque e-mail</h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink-secondary">
          Personnalisez l&apos;objet et le message de chaque e-mail automatique. Les variables entre accolades sont remplacées automatiquement à l&apos;envoi. Chaque confirmation client est aussi copiée (en copie cachée) à contact@appchallengeacademy.com et eric.crelier@orange.fr.
        </p>

        <div className="mt-6 space-y-6">
          {tpls.map((tp, i) => (
            <div key={tp.key} className="border border-white/10 bg-surface p-5" data-testid={`email-tpl-${tp.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-600 text-white"><Mail size={15} className="text-cyan" /> {tp.label}</div>
                <span className="text-[11px] text-ink-tertiary">Destinataire : {tp.to}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {tp.placeholders.map((ph) => (
                  <code key={ph} className="rounded bg-void px-2 py-0.5 text-[11px] text-cyan">{ph}</code>
                ))}
              </div>
              <label className="mt-4 block">
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Objet</span>
                <input value={tp.subject} onChange={(e) => setTpl(i, "subject", e.target.value)} data-testid={`email-tpl-subject-${tp.key}`}
                  className="w-full border border-white/10 bg-void px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
              </label>
              <label className="mt-3 block">
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Message</span>
                <textarea value={tp.body} onChange={(e) => setTpl(i, "body", e.target.value)} rows={10} data-testid={`email-tpl-body-${tp.key}`}
                  className="w-full resize-y border border-white/10 bg-void px-4 py-3 text-sm leading-relaxed text-white outline-none focus:border-cyan" />
              </label>
              <button onClick={() => saveTpl(tp)} disabled={savingTpl === tp.key} data-testid={`email-tpl-save-${tp.key}`}
                className="aca-btn-primary mt-3 inline-flex items-center gap-2 disabled:opacity-60">
                <Save size={15} /> {savingTpl === tp.key ? "Enregistrement..." : "Enregistrer ce modèle"}
              </button>
            </div>
          ))}
        </div>
      </div>
      </>
    </div>
  );
}
